<template>
  <div class="historico-job-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Configurações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <router-link :to="{ name: 'jobs-erp' }">Jobs</router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Histórico</span>
    </nav>

    <b-card class="mb-1" v-if="job">
      <b-row>
        <b-col md="6">
          <h5 class="mb-25">{{ job.nome }}</h5>
          <div class="text-muted small">{{ job.descricao || '—' }}</div>
        </b-col>
        <b-col md="6" class="text-right">
          <div class="small text-muted">Integração</div>
          <strong>{{ job.integracao_nome || '—' }}</strong>
          <div class="small text-muted">{{ job.integracao_metodo }} · {{ job.cron_expression || 'sem cron' }}</div>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div class="d-flex align-items-end mb-1 flex-wrap" style="gap:6px">
        <div>
          <label class="small text-muted d-block">Status</label>
          <b-form-select v-model="filtroStatus" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="SUCESSO">Sucesso</b-form-select-option>
            <b-form-select-option value="ERRO">Erro</b-form-select-option>
            <b-form-select-option value="EXECUTANDO">Executando</b-form-select-option>
          </b-form-select>
        </div>
        <div>
          <label class="small text-muted d-block">De</label>
          <b-form-input v-model="filtroDataInicio" type="datetime-local" size="sm" />
        </div>
        <div>
          <label class="small text-muted d-block">Até</label>
          <b-form-input v-model="filtroDataFim" type="datetime-local" size="sm" />
        </div>
        <b-button size="sm" variant="primary" @click="buscar">
          <feather-icon icon="SearchIcon" size="13" /> Filtrar
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
          <feather-icon icon="XIcon" size="13" />
        </b-button>
        <b-button size="sm" variant="outline-secondary" :to="{ name: 'jobs-erp' }" class="ml-auto">
          <feather-icon icon="ArrowLeftIcon" size="13" /> Voltar
        </b-button>
      </div>

      <div v-if="carregando" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!execucoes.length" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" /><br>
        Nenhuma execução registrada.
      </div>
      <b-table v-else :items="execucoes" :fields="fields" hover responsive small>
        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item.status)">{{ item.status }}</b-badge>
        </template>
        <template #cell(status_http)="{ item }">
          {{ item.status_http || '—' }}
        </template>
        <template #cell(tempo_ms)="{ item }">
          {{ item.tempo_ms ? `${item.tempo_ms} ms` : '—' }}
        </template>
        <template #cell(iniciado_em)="{ item }">
          {{ formatDate(item.iniciado_em) }}
        </template>
        <template #cell(origem)="{ item }">
          <b-badge :variant="item.origem === 'MANUAL' ? 'light-info' : 'light-primary'">{{ item.origem }}</b-badge>
        </template>
        <template #cell(mapeamento)="{ item }">
          <template v-if="item.mapeamento_status">
            <b-badge :variant="mapVariant(item.mapeamento_status)" :title="item.mapeamento_erro || ''">
              {{ item.mapeamento_status }}
            </b-badge>
            <div class="small text-muted" v-if="item.registros_lidos !== null && item.registros_lidos !== undefined">
              L:{{ item.registros_lidos || 0 }}
              <span class="text-success">+{{ item.registros_inseridos || 0 }}</span>
              <span class="text-info">~{{ item.registros_atualizados || 0 }}</span>
              <span v-if="item.registros_erro" class="text-danger">!{{ item.registros_erro }}</span>
            </div>
          </template>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(acoes)="{ item }">
          <b-button size="sm" variant="flat-info" title="Ver payload" @click="verPayload(item)">
            <feather-icon icon="EyeIcon" size="13" />
          </b-button>
          <b-button
            size="sm"
            :variant="item.status === 'ERRO' ? 'flat-danger' : 'flat-secondary'"
            title="Reprocessar com a resposta salva (não chama o ERP novamente)"
            :disabled="reexecutandoId === item.execucaoid"
            @click="reprocessar(item)"
          >
            <b-spinner small v-if="reexecutandoId === item.execucaoid" />
            <feather-icon v-else icon="RefreshCwIcon" size="13" />
          </b-button>
        </template>
      </b-table>

      <div v-if="meta.totalPages > 1" class="d-flex justify-content-end mt-1">
        <b-pagination v-model="currentPage" :total-rows="meta.total" :per-page="meta.pageSize"
                      size="sm" @change="onPageChange" />
      </div>
    </b-card>

    <b-modal v-model="showPayload" title="Payload da execução" size="lg" hide-footer scrollable>
      <div v-if="execAtual">
        <div class="mb-1 small text-muted">
          {{ execAtual.request_metodo }} {{ execAtual.request_url }} ·
          status {{ execAtual.status_http }} · {{ execAtual.tempo_ms }} ms
        </div>
        <pre v-if="execAtual.erro_mensagem" class="result-block result-block-error">{{ execAtual.erro_mensagem }}</pre>
        <pre class="result-block">{{ payloadFormatted }}</pre>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import JobErpClient from '@/api/Integracao/jobErp.client';

const client = new JobErpClient();

export default {
  name: 'HistoricoJob',
  data() {
    return {
      job: null,
      filtroStatus: null,
      filtroDataInicio: '',
      filtroDataFim: '',
      currentPage: 1,
      showPayload: false,
      execAtual: null,
      reexecutandoId: null,
      fields: [
        { key: 'execucaoid',    label: '#', thStyle: { width: '80px' } },
        { key: 'iniciado_em',   label: 'Início' },
        { key: 'origem',        label: 'Origem' },
        { key: 'status',        label: 'Status' },
        { key: 'status_http',   label: 'HTTP' },
        { key: 'tempo_ms',      label: 'Tempo' },
        { key: 'mapeamento',    label: 'Mapeamento' },
        { key: 'acoes',         label: '', class: 'text-right', thStyle: { width: '110px' } },
      ],
    };
  },
  computed: {
    ...mapState('JobErp', { execucoes: 'execucoes', meta: 'execucoesMeta', carregando: 'carregando' }),
    payloadFormatted() {
      if (!this.execAtual) return '';
      const r = this.execAtual.response_raw;
      if (!r) return '(sem corpo)';
      try { return JSON.stringify(JSON.parse(r), null, 2); } catch (e) { return r; }
    },
  },
  async created() {
    await this.carregarJob();
    await this.buscar();
  },
  methods: {
    ...mapActions('JobErp', ['listarExecucoes', 'buscarExecucao', 'buscarPorId']),
    async carregarJob() {
      try {
        this.job = await this.buscarPorId(this.$route.params.id);
      } catch (e) { /* mostra apenas histórico */ }
    },
    async buscar() {
      await this.listarExecucoes({
        id: this.$route.params.id,
        params: {
          status: this.filtroStatus || undefined,
          data_inicio: this.filtroDataInicio ? this.filtroDataInicio.replace('T', ' ') + ':00' : undefined,
          data_fim:    this.filtroDataFim    ? this.filtroDataFim.replace('T', ' ')    + ':59' : undefined,
          page: this.currentPage,
          pageSize: 20,
        },
      });
    },
    async limparFiltros() {
      this.filtroStatus = null;
      this.filtroDataInicio = '';
      this.filtroDataFim = '';
      this.currentPage = 1;
      await this.buscar();
    },
    onPageChange(page) { this.currentPage = page; this.buscar(); },
    mapVariant(s) {
      if (s === 'OK')      return 'light-success';
      if (s === 'PARCIAL') return 'light-warning';
      if (s === 'FALHA')   return 'light-danger';
      if (s === 'SKIPPED') return 'light-secondary';
      return 'light-secondary';
    },
    async verPayload(item) {
      const data = await this.buscarExecucao({ id: this.$route.params.id, execId: item.execucaoid });
      this.execAtual = data;
      this.showPayload = true;
    },
    async reprocessar(item) {
      this.reexecutandoId = item.execucaoid;
      try {
        const { data } = await client.reprocessar(this.$route.params.id, item.execucaoid);
        const st = data && data.data ? data.data.status : null;
        this.$bvToast.toast(`Reprocessado com a resposta salva: ${st || 'concluído'}`, {
          title: 'Reprocessamento',
          variant: st === 'ERRO' ? 'warning' : 'success',
          solid: true,
        });
        this.currentPage = 1;
        await this.buscar();
        await this.carregarJob();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || String(e);
        this.$bvToast.toast(`Falha ao reprocessar: ${msg}`, { title: 'Erro', variant: 'danger', solid: true });
      } finally {
        this.reexecutandoId = null;
      }
    },
    statusVariant(s) {
      if (s === 'SUCESSO') return 'light-success';
      if (s === 'ERRO') return 'light-danger';
      if (s === 'EXECUTANDO') return 'light-warning';
      return 'light-secondary';
    },
    formatDate(v) {
      if (!v) return '';
      try { return new Date(v).toLocaleString('pt-BR'); } catch (e) { return v; }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb { display: flex; align-items: center; font-size: 0.9rem; color: #6e6b7b; }
.page-breadcrumb a { color: #6e6b7b; }
.page-breadcrumb a:hover { color: #7367f0; }
.result-block {
  background: #f8f8f8;
  padding: 10px;
  border-radius: 4px;
  max-height: 360px;
  overflow: auto;
  font-size: 12px;
  white-space: pre-wrap;
}
.result-block-error { background: #fff3f3; color: #b00020; }
</style>
